import React, { useState, useEffect,useRef } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './SendApproval.scss';
import agent from '../../../agent';

const SendApproval = ({ estimate, mode = 'approval', clientEmail, approvalSendTo, approvalCc, clientBcc, defaultSubject, onClose, onSuccess, onError }) => {
    const [formData, setFormData] = useState({
        sentTo: '',
        emailHeader: '',
        emailBody: '',
        cc: '',
        bcc: ''
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generalError, setGeneralError] = useState('');
  
    const [attachments, setAttachments] = useState([]);
    const attachmentInputRef = useRef(null);

    // Quill editor configuration
    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            [{ 'align': [] }],
            ['clean']
        ],
    };

    const quillFormats = [
        'header', 'font', 'size',
        'bold', 'italic', 'underline', 'strike',
        'color', 'background',
        'script',
        'blockquote', 'code-block',
        'list', 'bullet', 'indent',
        'direction', 'align',
        'link', 'image', 'video'
    ];

    // Update form data when mode changes
    useEffect(() => {
        if (mode === 'approval') {
            setFormData(prev => ({
                ...prev,
                sentTo:      approvalSendTo || '',
                cc:          approvalCc     || '',
                bcc:         '',
                emailHeader: defaultSubject || ('Estimate Approval Request ' + estimate.estimateNumber),

                emailBody:   ''
            }));
        } else if (mode === 'client') {
            const customerName = estimate?.customerNameEmail;
            console.log( estimate);
           
            setFormData(prev => ({
                ...prev,
                sentTo:      clientEmail || '',
                cc:          '',
                bcc:         clientBcc  || '',
                emailHeader: defaultSubject || 'Estimate from Zavza Seal LLC',
                emailBody:   `<p>Dear ${customerName},</p><br>
                    <p>Thank you for considering our services for your project. Attached is our estimate and a detailed description of the work we propose to undertake.</p>
                    <br><p>To proceed with the project, kindly sign the last page of the estimate below the project price area and email it back to us.</p>
                    <br><p>Should you need to finance this project please contact our office for possible financing options.</p>
                    <br><p>Please review and feel free to contact us if you have any questions or concerns. We look forward to the opportunity to work with you.</p>
                    <br><p>Warm regards,</p>
                    `
            }));
        
        }
    }, [mode, approvalSendTo, approvalCc, clientEmail, clientBcc, defaultSubject]);
    
    
    useEffect(() => {
        if (defaultSubject) {
            setFormData(prev => ({ ...prev, emailHeader: defaultSubject }));
        }
    }, [defaultSubject]);

    const handleInputChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    
        // Clear general error when user starts typing
        if (generalError) {
            setGeneralError('');
        }
    };
  
    const handleAttachmentAdd = (e) => {
      const files = Array.from(e.target.files);
        if (!files.length) return;
            setAttachments(prev => [
            ...prev,
            ...files.map(file => ({
                id: `${Date.now()}_${Math.random()}`,
                file,
                name: file.name,
                size: file.size
            }))
        ]);
        // Reset input so same file can be re-added if needed
        e.target.value = '';
    };
    
    const handleAttachmentRemove = (id) => {
      setAttachments(prev => prev.filter(a => a.id !== id));
    };
    
    const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };


    const validateForm = () => {
        const newErrors = {};
    
        if (!formData.sentTo.trim()) {
            newErrors.sentTo = 'Recipient email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.sentTo)) {
            newErrors.sentTo = 'Please enter a valid email address';
        }
    
        if (!formData.emailHeader.trim()) {
            newErrors.emailHeader = 'Email subject is required';
        }
    
        // Strip HTML tags for validation
        const emailBodyText = formData.emailBody.replace(/<[^>]*>/g, '').trim();
        if (!emailBodyText) {
            newErrors.emailBody = 'Email body is required';
        }
    
        // Validate CC emails if provided
        if (formData.cc.trim()) {
            const ccEmails = formData.cc.split(',').map(email => email.trim()).filter(email => email);
            const invalidCC = ccEmails.some(email => !/\S+@\S+\.\S+/.test(email));
            if (invalidCC) {
                newErrors.cc = 'Please enter valid email addresses separated by commas';
            }
        }
    
        // Validate BCC emails if provided
        if (formData.bcc.trim()) {
            const bccEmails = formData.bcc.split(',').map(email => email.trim()).filter(email => email);
            const invalidBCC = bccEmails.some(email => !/\S+@\S+\.\S+/.test(email));
            if (invalidBCC) {
                newErrors.bcc = 'Please enter valid email addresses separated by commas';
            }
        }
    
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
    
        const validationErrors = validateForm();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            setGeneralError('');
            return;
        }
    
        setGeneralError('');
        setIsSubmitting(true);
    
        try {
            const formDataToSend = new FormData();
            formDataToSend.append('estimateId',  estimate?.id);
            formDataToSend.append('sentTo',      formData.sentTo);
            formDataToSend.append('emailHeader', formData.emailHeader);
            formDataToSend.append('emailBody',   formData.emailBody);
            formDataToSend.append('cc',  JSON.stringify(formData.cc.split(',').map(e => e.trim()).filter(Boolean)));
            formDataToSend.append('bcc', JSON.stringify(formData.bcc.split(',').map(e => e.trim()).filter(Boolean)));
            formDataToSend.append('mode', mode);
    
            attachments.forEach((a, i) => {
                formDataToSend.append(`attachments`, a.file);
            });
    
            const response = await agent.estimates.sendEstimateEmail(formDataToSend);
    
            if (response?.data?.email?.success) {
                onSuccess();
            } else {
                setGeneralError('Failed to send email. Please try again.');
            }
        } catch (error) {
            setGeneralError('Failed to send email. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            onClose();
        }
    };

    return (
        <div className="send-approval-overlay" style={{ display: 'flex' }}>
            <div className="send-approval-modal">
                <div className="send-approval-header">
                    <h3>{mode === 'client' ? 'Send to Client' : 'Send for Approval'}</h3>
                    <button 
                        type="button" 
                        className="send-approval-close" 
                        onClick={handleClose}
                        disabled={isSubmitting}
                    >
                    ✕
                    </button>
                </div>
        
                {/* General Error Message */}
                {generalError && (
                    <div className="send-approval-error-banner">
                        <div className="send-approval-error-content">
                            <span className="send-approval-error-icon">⚠️</span>
                            <span className="send-approval-error-text">{generalError}</span>
                            <button 
                                type="button" 
                                className="send-approval-error-close"
                                onClick={() => setGeneralError('')}
                                disabled={isSubmitting}
                                title="Dismiss error"
                            >
                            ✕
                            </button>
                        </div>
                    </div>
                )}
        
                <form onSubmit={handleSubmit} className="send-approval-form">
                    <div className="send-approval-content">
            
                        {/* Recipient Email */}
                        <div className="send-approval-field">
                            <label>Send To *</label>
                            <div className="send-approval-input-wrapper">
                                <span className="send-approval-icon">📧</span>
                                <input
                                    type="email"
                                    value={formData.sentTo}
                                    onChange={(e) => handleInputChange('sentTo', e.target.value)}
                                    className={errors.sentTo ? 'error' : ''}
                                    placeholder="email@example.com"
                                    disabled={isSubmitting}
                                />
                            </div>
                            {errors.sentTo && <span className="error-message">{errors.sentTo}</span>}
                        </div>

                        {/* CC */}
                        <div className="send-approval-field">
                            <label>CC (Optional)</label>
                            <div className="send-approval-input-wrapper">
                                <span className="send-approval-icon">📋</span>
                                <input
                                    type="text"
                                    value={formData.cc}
                                    onChange={(e) => handleInputChange('cc', e.target.value)}
                                    className={errors.cc ? 'error' : ''}
                                    placeholder="cc1@example.com, cc2@example.com"
                                    disabled={isSubmitting}
                                />
                            </div>
                            {errors.cc && <span className="error-message">{errors.cc}</span>}
                        </div>

                        {/* BCC */}
                        <div className="send-approval-field">
                            <label>BCC (Optional)</label>
                            <div className="send-approval-input-wrapper">
                                <span className="send-approval-icon">👁️</span>
                                <input
                                    type="text"
                                    value={formData.bcc}
                                    onChange={(e) => handleInputChange('bcc', e.target.value)}
                                    className={errors.bcc ? 'error' : ''}
                                    placeholder="bcc1@example.com, bcc2@example.com"
                                    disabled={isSubmitting}
                                />
                            </div>
                            {errors.bcc && <span className="error-message">{errors.bcc}</span>}
                        </div>

                        {/* Email Subject */}
                        <div className="send-approval-field">
                            <label>Email Subject *</label>
                            <div className="send-approval-input-wrapper">
                                <span className="send-approval-icon">📝</span>
                                <input
                                    type="text"
                                    value={formData.emailHeader}
                                    onChange={(e) => handleInputChange('emailHeader', e.target.value)}
                                    className={errors.emailHeader ? 'error' : ''}
                                    placeholder={defaultSubject || (mode === 'client' ? 'Estimate from Zavza Seal LLC' : 'Estimate Approval Request')}
                                    disabled={isSubmitting}
                                />
                            </div>
                            {errors.emailHeader && <span className="error-message">{errors.emailHeader}</span>}
                        </div>

                        {/* Email Body */}
                        <div className="send-approval-field">
                            <label>Email Body *</label>
                            <div className="send-approval-editor-container">
                                <ReactQuill
                                    theme="snow"
                                    value={formData.emailBody}
                                    onChange={(value) => handleInputChange('emailBody', value)}
                                    modules={quillModules}
                                    formats={quillFormats}
                                    placeholder="Enter your email message..."
                                    readOnly={isSubmitting}
                                    className={errors.emailBody ? 'error' : ''}
                                />
                            </div>
                            {errors.emailBody && <span className="error-message">{errors.emailBody}</span>}
                        </div>
            
                        {/* Attachments */}
                        <div className="send-approval-field">
                            <label>Attachments (Optional)</label>
                            <div className="send-approval-attachments">
                                <input
                                    type="file"
                                    id="send-approval-file-input"
                                    ref={attachmentInputRef}
                                    multiple
                                    onChange={handleAttachmentAdd}
                                    disabled={isSubmitting}
                                    style={{ display: 'none' }}
                                />
                                <label
                                    htmlFor="send-approval-file-input"
                                    className={`send-approval-attach-btn ${isSubmitting ? 'disabled' : ''}`}
                                >
                                📎 Add Attachments
                                </label>
                
                                {attachments.length > 0 && (
                                <div className="send-approval-attachment-list">
                                {attachments.map(a => (
                                    <div key={a.id} className="send-approval-attachment-item">
                                        <span className="send-approval-attachment-icon">📄</span>
                                        <span className="send-approval-attachment-name" title={a.name}>
                                            {a.name}
                                        </span>
                                        <span className="send-approval-attachment-size">
                                            {formatFileSize(a.size)}
                                        </span>
                                        <button
                                            type="button"
                                            className="send-approval-attachment-remove"
                                            onClick={() => handleAttachmentRemove(a.id)}
                                            disabled={isSubmitting}
                                            title="Remove attachment"
                                        >
                                        ✕
                                        </button>
                                    </div>
                                ))}
                                </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="send-approval-actions">
                        <button 
                            type="button" 
                            className="send-approval-cancel" 
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                        Cancel
                        </button>
                        <button 
                            type="submit" 
                            className="send-approval-send" 
                            disabled={isSubmitting}
                        >
                        {isSubmitting ? (
                        <>
                            <span className="send-approval-loading">⟳</span>
                                Sending...
                        </>
                        ) : (
                            mode === 'client' ? '👤 Send to Client' : '📧 Send for Approval'
                        )}    
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SendApproval;
